import type { DeepPartial } from "simplytyped";
import { z } from "zod";

export const AccountingZod = z.object({
  // account_holder: z.string().optional(),
  account_id: z.number().optional(),
  // addressline1: z.string().optional(),
  // addressline2: z.string().optional(),
  // bic: z.string().optional(),
  // city: z.string().optional(),
  company: z.string().optional(),
  // contract_start: z.number().optional(),
  country: z.string().optional(),
  // employees: z.string().optional(),
  // firstname: z.string().optional(),
  // iban: z.string().optional(),
  // id: z.number(),
  // invoice_email: z.string().email().optional(),
  // invoice_email2: z.string().optional(),
  // invoice_email3: z.string().optional(),
  // invoice_period: z.object({
  //   from: z.number(),
  //   until: z.number(),
  // }),
  // lastname: z.string().optional(),
  // ordernr: z.string().optional(),
  // payment_method: z.string().optional(),
  // vat: z.string().optional(),
  // vertical: z.string().optional(),
  // zip: z.string().optional(),
});

export type Accounting = z.infer<typeof AccountingZod>;

export const AccountInfoZod = z.object({
  id: z.number(),
  testphase_finished: z.boolean().optional(),
  gdpr_needed: z.boolean().optional(),
  gdpr_accepted: z.boolean().optional(),
  contract_start: z.number().optional(),
  testphase_start: z.number().optional(),
  testphase_end: z.number().optional(),
  paymentBlock: z
    .object({
      blocked: z.boolean().optional(),
      amount: z.number().optional(),
      block_date: z.number().optional(),
    })
    .optional(),
});

export type AccountInfo = DeepPartial<z.infer<typeof AccountInfoZod>>;

const UserSettingsZod = z.preprocess(
  // MCP api returns an empty array for the settings key if the user has no
  // settings currenly set. In that case we return an empty object instead.
  // This type is also partially used for the update stream user update message.
  // It has the same issue.
  (value) => (value instanceof Array ? {} : value),
  z
    .object({
      allowNotifications: z.number(),
      allowNotificationSound: z.number(),
      customNotificationAudioVolume: z.number().nullable(),
      customNotificationAudio: z.string().nullable(),
      pushReceivedChat: z.number(),
      pushAssignedTicket: z.number(),
      pushNewTicket: z.number(),
    })
    .deepPartial()
    .passthrough()
);

const Permissions = [
  "administrate",
  "agentCreate",
  "agentDeleteOthers",
  "agentEditOthers",
  "agentGetOthers",
  "botCreateDraft",
  "botEdit",
  "botEditStatus",
  "botGet",
  "botRemove",
  "chatDelete",
  "chatRead",
  "chatWrite",
  "chatWriteMultiple",
  "contentEdit",
  "contentGet",
  "crmCustomerAddField",
  "crmCustomerAddLabel",
  "crmCustomerAddTeam",
  "crmCustomerEditField",
  "crmCustomerEditLabel",
  "crmCustomerEditTeam",
  "crmCustomerReadField",
  "crmCustomerReadLimitedField",
  "crmCustomerRemoveField",
  "crmCustomerRemoveLabel",
  "crmCustomerRemoveTeam",
  "settingsChannel",
  "settingsSkills",
  "statisticsAgents",
  "statisticsChats",
  "statisticsClicks",
  "statisticsContent",
  "statisticsTickets",
  "statisticsUser",
  "targetingEdit",
  "targetingRead",
  "ticketCloseOthers",
  "ticketEditOthers",
  "ticketOpen",
  "ticketRead",
  "ticketReadClosed",
  "ticketReadOthers",
  "ticketReopenOthers",
  "ticketShowAssignment",
  "ticketShowFilter",
  "userBlock",
  "userEdit",
  "userGet",
  "userRemove",
  "userShowList",
  "userStart",
  "userStop",
] as const;

export type Permission = (typeof Permissions)[number];

export enum UserStatus {
  Available = 1,
  NotAvailable = 2,
}

const ProjectZod = z.object({
  image: z.string(),
  name: z.string(),
  project_id: z.number(),
  uiorder: z.number(),
});

export type Project = z.infer<typeof ProjectZod>;

export const UserMeZod = z
  .object({
    /** @deprecated Should use activeProject instead, wherever possible. */
    activeChannel: z.number(),
    activeProject: z.number(),
    /** Terminology change: Channel (MCP) -> Project (Sinch Engage) */
    channels: z.record(z.string(), ProjectZod),
    email: z.string(),
    id: z.number(),
    language: z.string(),
    created: z.number(),
    name: z.string(),
    profile_image: z.string(),
    roles: z.string().array(),
    rights: z.enum(Permissions).array(),
    settings: UserSettingsZod,
    status: z.nativeEnum(UserStatus),
  })
  .passthrough();

export type UserMe = z.infer<typeof UserMeZod>;
export type UserMePartial = Omit<
  DeepPartial<UserMe>,
  "id" | "name" | "email" | "rights"
> &
  Pick<UserMe, "id" | "name" | "email" | "rights">;
